import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import atlasLogo from "../../assets/logo/atlas-logo.png";
import { emailResetPassword } from "../../api/login/loginApi";
import CinemaIcon from "../cinema/CinemaIcon";
import { getRecoveryCopy } from "./recoveryCopy";
import "./forgotPassword.scss";

const ForgotPassword = () => {
  const copy = getRecoveryCopy();
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [requestError, setRequestError] = useState("");
  const pending = useRef(false);
  const mounted = useRef(true);
  const titleRef = useRef(null);
  const editingEmail = useRef(false);
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors }
  } = useForm();

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  useEffect(() => {
    if (sentTo) {
      if (titleRef.current) titleRef.current.focus();
    } else if (editingEmail.current) {
      setFocus("email");
      editingEmail.current = false;
    }
  }, [sentTo, setFocus]);

  const handleForgotPassword = async ({ email }) => {
    if (pending.current) return;
    if (navigator.onLine === false) {
      setRequestError(copy.offline);
      return;
    }
    pending.current = true;
    setSending(true);
    setRequestError("");
    try {
      await emailResetPassword(email);
      if (mounted.current) setSentTo(email);
    } catch (error) {
      if (mounted.current)
        setRequestError(navigator.onLine === false ? copy.offline : copy.error);
    } finally {
      pending.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return (
    <div className="atlas-recovery">
      <header className="atlas-recovery-header">
        <Link to="/login" className="atlas-recovery-brand" aria-label="Atlas">
          <img src={atlasLogo} alt="" />
        </Link>
        <Link to="/login" className="atlas-recovery-back">
          <CinemaIcon name="previous" />
          {copy.back}
        </Link>
      </header>
      <main className="atlas-recovery-main">
        <section className="atlas-recovery-story" aria-label={copy.storyLabel}>
          <div className="atlas-recovery-orbits" aria-hidden="true">
            <span />
            <span />
            <i />
          </div>
          <p className="atlas-recovery-eyebrow">
            <span />
            {copy.eyebrow}
          </p>
          <h2>
            {copy.storyTitle}
            <br />
            <em>{copy.storyAccent}</em>
          </h2>
          <p className="atlas-recovery-story-note">{copy.storyNote}</p>
          <div className="atlas-recovery-story-line" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        </section>
        <section
          className="atlas-recovery-card"
          aria-labelledby="recovery-title"
        >
          <div className="atlas-recovery-emblem" aria-hidden="true">
            <CinemaIcon name={sentTo ? "check" : "key"} />
          </div>
          <p className="atlas-recovery-eyebrow">
            {sentTo ? copy.sentEyebrow : copy.cardEyebrow}
          </p>
          <h1 id="recovery-title" ref={titleRef} tabIndex={-1}>
            {sentTo ? copy.sentTitle : copy.title}
          </h1>
          {sentTo ? (
            <>
              <p className="atlas-recovery-description" role="status">
                {copy.sentNote}
              </p>
              <div className="atlas-recovery-recipient">
                <CinemaIcon name="mail" />
                <span>{sentTo}</span>
              </div>
              <p className="atlas-recovery-hint">{copy.spamHint}</p>
              <Link to="/login" className="atlas-recovery-submit">
                {copy.back}
                <CinemaIcon name="arrow" />
              </Link>
              <button
                type="button"
                className="atlas-recovery-edit"
                onClick={() => {
                  editingEmail.current = true;
                  setSentTo("");
                }}
              >
                {copy.anotherEmail}
              </button>
            </>
          ) : (
            <>
              <p
                className="atlas-recovery-description"
                id="recovery-description"
              >
                {copy.note}
              </p>
              <form
                noValidate
                onSubmit={handleSubmit(handleForgotPassword)}
                aria-busy={sending}
              >
                <label htmlFor="recovery-email">{copy.email}</label>
                <div
                  className={`atlas-recovery-field${
                    errors.email ? " has-error" : ""
                  }`}
                >
                  <CinemaIcon name="mail" />
                  <input
                    id="recovery-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder={copy.placeholder}
                    readOnly={sending}
                    required
                    aria-invalid={!!errors.email}
                    aria-describedby={
                      errors.email
                        ? "recovery-email-error"
                        : "recovery-description"
                    }
                    {...register("email", {
                      setValueAs: (value) => value.trim(),
                      required: copy.required,
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: copy.invalid
                      }
                    })}
                  />
                </div>
                {errors.email && (
                  <p
                    id="recovery-email-error"
                    className="atlas-recovery-error"
                    role="alert"
                  >
                    {errors.email.message}
                  </p>
                )}
                {requestError && (
                  <p className="atlas-recovery-request-error" role="alert">
                    <CinemaIcon name="info" />
                    {requestError}
                  </p>
                )}
                <button
                  type="submit"
                  className="atlas-recovery-submit"
                  disabled={sending}
                >
                  {sending ? (
                    <>
                      <span
                        className="atlas-recovery-spinner"
                        aria-hidden="true"
                      />
                      <span role="status">{copy.sending}</span>
                    </>
                  ) : (
                    <>
                      {requestError ? copy.retry : copy.submit}
                      <CinemaIcon name="arrow" />
                    </>
                  )}
                </button>
              </form>
              <p className="atlas-recovery-hint">
                <CinemaIcon name="shield" />
                {copy.hint}
              </p>
            </>
          )}
        </section>
      </main>
      <footer className="atlas-recovery-footer">
        <span />
        {copy.signature}
        <span />
      </footer>
    </div>
  );
};

export default ForgotPassword;
